import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Loader2, 
  CheckCircle2,
  Clock,
  Download,
  ArrowRight,
  User,
  Briefcase,
  MapPin,
  UserCheck,
  Star,
  TrendingUp,
  Target,
  LogOut
} from "lucide-react";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TalentSummaryContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const storedUser = localStorage.getItem("userInfo");
    
    if (!token) {
      navigate("/login");
      return;
    }
    
    if (storedUser) {
      setUserInfo(JSON.parse(storedUser));
    }
    
    fetchAssessment();
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
  });

  const fetchAssessment = async () => {
    try {
      const response = await axios.get(`${API}/talent/my-assessment`, getAuthHeaders());
      setAssessment(response.data);
    } catch (err) {
      console.error("Error fetching assessment:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
      } else if (err.response?.status === 404) {
        toast.error("No assessment found for your account");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleContinueAssessment = () => {
    if (assessment?.assessmentToken) {
      // Redirect to dynamic assessment form instead of old hardcoded form
      navigate(`/dynamic-assessment/${assessment.assessmentToken}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#1a365d] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your assessment...</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No assessment found for your account.</p>
            <Button onClick={handleLogout} className="mt-4">
              Return to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCompleted = assessment.status === 'completed';
  const isInProgress = assessment.status === 'started';

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1a365d] text-white shadow-lg">
        <div className="max-w-[900px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <div>
                <h1 className="text-lg font-semibold font-['Manrope']">
                  My Assessment
                </h1>
                <p className="text-sm text-white/70">
                  Technology Capability Assessment
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {userInfo && (
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium">{userInfo.fullName}</p>
                </div>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 py-8">
        {/* Status Banner */}
        {isCompleted ? (
          <Card className="bg-emerald-50 border-emerald-200 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-emerald-800">
                    Assessment Submitted
                  </h2>
                  <p className="text-sm text-emerald-700 mt-1">
                    Submitted: {assessment.submittedAt ? new Date(assessment.submittedAt).toLocaleString() : 'Recently'}
                  </p>
                  {assessment.confirmationId && (
                    <p className="text-sm text-emerald-600 mt-1">
                      Confirmation: <span className="font-mono font-medium">{assessment.confirmationId}</span>
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-amber-50 border-amber-200 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-amber-800">
                    Assessment In Progress
                  </h2>
                  <div className="flex items-center gap-3 mt-2">
                    <Progress value={assessment.progressPercentage} className="flex-1 h-2" />
                    <span className="text-sm font-medium text-amber-700">
                      {assessment.progressPercentage}%
                    </span>
                  </div>
                  <Button 
                    className="mt-4 bg-amber-600 hover:bg-amber-700"
                    onClick={handleContinueAssessment}
                  >
                    Continue My Assessment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Card */}
        <Card className="bg-white shadow-sm mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1a365d]">
              Your Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="font-medium">{assessment.profile?.fullName}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Title</p>
                  <p className="font-medium">{assessment.profile?.jobTitle || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Region</p>
                  <p className="font-medium">{assessment.profile?.regionFlag} {assessment.profile?.region}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Manager</p>
                  <p className="font-medium">{assessment.profile?.managerName || 'N/A'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Highlights (only if completed) */}
        {isCompleted && (
          <Card className="bg-white shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#1a365d]">
                Highlights from Your Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Top Skills */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-amber-500" />
                  <h3 className="font-medium text-sm">Top Skills (You rated 4-5)</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {assessment.highlights?.topSkills?.length > 0 ? (
                    assessment.highlights.topSkills.map((skill, i) => (
                      <Badge key={i} variant="secondary" className="bg-amber-100 text-amber-800">
                        {skill}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No top skills identified yet</p>
                  )}
                </div>
              </div>

              {/* Growth Interests */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <h3 className="font-medium text-sm">Growth Interests</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {assessment.highlights?.growthInterests?.length > 0 ? (
                    assessment.highlights.growthInterests.map((interest, i) => (
                      <Badge key={i} variant="secondary" className="bg-emerald-100 text-emerald-800">
                        {interest}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No growth interests identified yet</p>
                  )}
                </div>
              </div>

              {/* Career Goal */}
              {assessment.highlights?.careerGoal && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-blue-500" />
                    <h3 className="font-medium text-sm">Career Goal (3-5 years)</h3>
                  </div>
                  <p className="text-sm bg-blue-50 p-3 rounded-lg text-blue-800">
                    {assessment.highlights.careerGoal}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Next Steps */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1a365d]">
              Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isCompleted ? (
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-xs font-medium text-emerald-600">
                    ✓
                  </div>
                  <div>
                    <p className="font-medium text-sm">Assessment Submitted</p>
                    <p className="text-xs text-muted-foreground">Your responses have been recorded</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#1a365d]/10 rounded-full flex items-center justify-center text-xs font-medium text-[#1a365d]">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-sm">Manager Review</p>
                    <p className="text-xs text-muted-foreground">Your manager will review your assessment</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#1a365d]/10 rounded-full flex items-center justify-center text-xs font-medium text-[#1a365d]">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-sm">Technical Testing</p>
                    <p className="text-xs text-muted-foreground">You may be invited for technical assessment</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#1a365d]/10 rounded-full flex items-center justify-center text-xs font-medium text-[#1a365d]">
                    4
                  </div>
                  <div>
                    <p className="font-medium text-sm">Results Shared</p>
                    <p className="text-xs text-muted-foreground">Final results by April 2026</p>
                  </div>
                </li>
              </ol>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-4">
                  Complete your assessment to see next steps
                </p>
                <Button onClick={handleContinueAssessment}>
                  Continue My Assessment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Download Button (only if completed) */}
        {isCompleted && (
          <div className="mt-6 flex justify-center">
            <Button variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Download My Summary PDF
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

// Export wrapped with MaintenanceOverlay
export default function TalentSummary() {
  return (
    <MaintenanceOverlay>
      <TalentSummaryContent />
    </MaintenanceOverlay>
  );
}
